import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Box, TablePagination,
  Button, TextField, MenuItem
} from '@mui/material';
import AddUserDialog from './AddUserDialog';
import { useNavigate } from 'react-router-dom';

const UserList = () => {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [search, setSearch] = useState('');
  const [carSearch, setCarSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const navigate = useNavigate();

  const STATUS_OPTIONS = [
    'SUBSCRIBED', 'cancelled', 'ASK FOR PLAN', 'CONVERT CURRENT WASHER', 'demo done',
    'no service required', 'has regular guy', 'DEMO SCHEDULED', 'FOLLOW UP', 'NO RESPONSE',
    'ON DEMAND', 'PRICE REQUESTED', 'RAMESH', 'SEND SUB. DET.', 'WASHER N/A', 'WAITING LIST',
    'NOT NEEDED', 'CAR SOLD'
  ];

  const updateUserStatus = async (userId, status, revertCallback) => {
    // optimistic update already applied by caller
    if (!window.confirm('Are you sure you want to Update this status?')) return;
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    try {
      await fetch(`${process.env.REACT_APP_SERVER_URI}/users/statusUpdate/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: JSON.stringify({ status }),
      });
    } catch (err) {
      if (revertCallback) revertCallback();
      alert('Failed to update status.');
    }
  };

  useEffect(() => {
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;

    let url = `${process.env.REACT_APP_SERVER_URI}/users/list?page=${page + 1}&limit=${rowsPerPage}`;
    if (search) url += `&searchRegex=${encodeURIComponent(search)}`;
    if (carSearch) url += `&carRegex=${encodeURIComponent(carSearch)}`;
    if (statusFilter) url += `&status=${encodeURIComponent(statusFilter)}`;
    fetch(url, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    })
      .then(res => res.json())
      .then(result => {
        console.log(result.data)
        setUsers(result.data || []);
        setTotal(result.total || 0);
      })
      .catch(() => {
        setUsers([]);
        setTotal(0);
      });
  }, [page, rowsPerPage, search, carSearch, statusFilter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleEditOpen = (user) => {
    setEditUser(user);
    setAddOpen(true);
  };

  const handleAddClose = () => {
    setAddOpen(false);
    setEditUser(null);
  };

  const handleDelete = (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    fetch(`${process.env.REACT_APP_SERVER_URI}/users/delete/${userId}`, {
      method: 'DELETE',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    })
      .then(res => res.json())
      .then(() => {
        setUsers(users => users.filter(u => u._id !== userId));
        setTotal(t => t - 1);
      })
      .catch(() => {
        alert('Failed to delete user.');
      });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>User Management</Typography>
        <TextField
          select
          label="Filter by status"
          value={statusFilter}
          onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
          size="small"
          sx={{ mr: 2, minWidth: 220 }}
        >
          <MenuItem value="">All</MenuItem>
          {STATUS_OPTIONS.map(opt => (
            <MenuItem key={opt} value={opt}>{opt}</MenuItem>
          ))}
        </TextField>
        <TextField
          label="Name/Phone search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="small"
          sx={{ mr: 2, minWidth: 200 }}
        />
         <TextField
          label="Search by car"
          value={carSearch}
          onChange={e => setCarSearch(e.target.value)}
          size="small"
          sx={{ mr: 2, minWidth: 200 }}
        />
        <Button variant="contained" onClick={() => setAddOpen(true)}>Add User</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Phone</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell>Cars</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(user => (
              <TableRow key={user._id}>
                <TableCell>
                  <span
                    style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => navigate(`/users/${user._id}`)}
                  >
                    {user.name}
                  </span>
                </TableCell>
                <TableCell>{user.phone}</TableCell>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    value={user.status || ''}
                    onChange={e => {
                      const newStatus = e.target.value;
                      // optimistic update
                      const prev = users;
                      setUsers(prevUsers => prevUsers.map(u => u._id === user._id ? { ...u, status: newStatus } : u));
                      updateUserStatus(user._id, newStatus, () => {
                        // revert on failure
                        setUsers(prev);
                      });
                    }}
                    fullWidth
                  >
                    <MenuItem value="">(none)</MenuItem>
                    {STATUS_OPTIONS.map(opt => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell>{user.notes}</TableCell>
                <TableCell>
                  {Array.isArray(user.cars) && user.cars.length > 0
                    ? user.cars.map(car => car.regNo).filter(Boolean).join(', ')
                    : '-'}
                </TableCell>
                <TableCell>
                  <Button variant="outlined" size="small" onClick={() => handleEditOpen(user)} sx={{ mr: 1 }}>
                    Edit
                  </Button>
                  <Button variant="outlined" size="small" color="error" onClick={() => handleDelete(user._id)}>
                    Delete
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>
      {/* Add/Edit User Dialog */}
      <AddUserDialog
        open={addOpen}
        onClose={handleAddClose}
        onUserAdded={() => setPage(0)}
        user={editUser}
      />
    </Box>
  );
};

export default UserList;
